import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, BLUE_LIGHT_DISABLED, RED_DARK} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import ControlledNumericInput from 'ui-library/inputs/ControlledNumericInput';
import {NumericInputChangeEvent} from 'util/Types';

export interface NumericButtonInputState {
  value: number | null;
  isValid: boolean;
}

export interface NumericButtonInputProps {
  value: number | null;
  // Called when the value is changed via the plus/minus buttons or the input.
  onChangeValue: (state: NumericButtonInputState) => void;
  disabled?: boolean;
  required?: boolean;
  // Min and max values are inclusive
  minValue?: number;
  maxValue?: number;
  // Value to use when the current value is null and the plus/minus buttons are clicked.
  // The default must be valid within the given min and max values.
  defaultValue?: number;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * A controlled numeric input component that can also be controlled by plus and minus buttons.
 */
export default function NumericButtonInput({
  value,
  onChangeValue,
  disabled = false,
  required = false,
  minValue,
  maxValue,
  defaultValue = 0,
  dataTestAttrs,
}: NumericButtonInputProps): JSX.Element {
  const canDecrease = minValue == null || value == null || value > minValue;
  const canIncrease = maxValue == null || value == null || value < maxValue;

  const possibleErrorMessage = (() => {
    if (minValue != null && maxValue != null) {
      return `Value must fall between ${minValue} and ${maxValue}.`;
    }
    if (minValue != null) {
      return `Value cannot be lower than ${minValue}.`;
    }
    if (maxValue != null) {
      return `Value cannot exceed ${maxValue}.`;
    }
    return null;
  })();

  function isInvalid(val: number | null): boolean {
    if (val == null) {
      return required;
    }
    return (minValue != null && val < minValue) || (maxValue != null && val > maxValue);
  }

  function handleClickMinus(): void {
    // Will only be called if the new decreased value is valid.
    onChangeValue({isValid: true, value: value == null ? defaultValue : value - 1});
  }

  function handleClickPlus(): void {
    // Will only be called if the new increased value is valid.
    onChangeValue({isValid: true, value: value == null ? defaultValue : value + 1});
  }

  function handleInputChange(e: NumericInputChangeEvent): void {
    onChangeValue({isValid: !isInvalid(e.target.numericValue), value: e.target.numericValue});
  }

  return (
    <div {...getTestProps(dataTestAttrs)}>
      <div className={css(styles.buttonInputContainer)}>
        <Tooltip
          isDisabled={disabled || minValue == null || canDecrease}
          text={`Value cannot be lower than ${minValue}.`}
        >
          <Button
            icon="minus"
            aria-label="Decrease value"
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            flexWidth={false}
            size={ButtonSize.SMALL}
            onClick={handleClickMinus}
            disabled={disabled || !canDecrease}
          />
        </Tooltip>
        <Tooltip isDisabled={!disabled} text="Value cannot be changed.">
          <ControlledNumericInput
            className={css(
              styles.input,
              disabled && styles.disabledInput,
              isInvalid(value) && styles.errorInput
            )}
            value={value}
            onChange={handleInputChange}
            isDisabled={disabled}
            validateInteger={true}
          />
        </Tooltip>
        <Tooltip
          isDisabled={disabled || maxValue == null || canIncrease}
          text={`Value cannot exceed ${maxValue}.`}
        >
          <Button
            icon="compose"
            aria-label="Increase value"
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            flexWidth={false}
            size={ButtonSize.SMALL}
            onClick={handleClickPlus}
            disabled={disabled || !canIncrease}
          />
        </Tooltip>
      </div>
      {isInvalid(value) && possibleErrorMessage && (
        <p className={css(styles.errorMessage)}>{possibleErrorMessage}</p>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  buttonInputContainer: {
    display: 'flex',
  },
  input: {
    border: `1px solid ${BLUE_LIGHT}`,
    margin: '0 6px',
    width: 60,
  },
  disabledInput: {
    border: `1px solid ${BLUE_LIGHT_DISABLED}`,
  },
  errorInput: {
    border: `1px solid ${RED_DARK}`,
  },
  errorMessage: {
    color: RED_DARK,
    marginTop: 4,
  },
});
